import React from "react";
import { astryx, DataTable, SectionHeader, Sparkles } from "@shadowcue/ui";
import type { IUser } from "../../types/iuser";
import type { IDashboard, IUserConversation } from "../../types/idashboard";

interface UserConversationsProps {
  currentUser: IUser;
  dashboard: IDashboard;
  onPageChange: (page: number) => void;
}

/**
 * A page of the viewer's own conversations, newest first: name, score and
 * date, nothing else. Same table as the script templates above it; names
 * link to the conversation summary.
 */
const UserConversations = ({ currentUser, dashboard, onPageChange }: UserConversationsProps) => {
  const { page, pages } = dashboard.userConversationsPagination;

  const columns: astryx.TableColumn<IUserConversation>[] = [
    {
      key: "name",
      header: "Name",
      width: astryx.proportional(2),
      renderCell: (conversation) => (
        <astryx.Link href={`/dashboard/conversations/${conversation.id}`}>
          {conversation.name}
        </astryx.Link>
      ),
    },
    {
      key: "score",
      header: "Score",
      width: astryx.pixel(110),
      align: "end",
      renderCell: (conversation) =>
        conversation.score ? (
          `${conversation.score.pct}%`
        ) : (
          <astryx.Text type="supporting" color="secondary">
            Not scored
          </astryx.Text>
        ),
    },
    {
      key: "createdAt",
      header: "Date",
      width: astryx.pixel(140),
      align: "end",
      renderCell: (conversation) => (
        <astryx.Timestamp value={conversation.createdAt} format="date" hasTooltip={false} />
      ),
    },
  ];

  return (
    <astryx.Card variant="default">
      <astryx.VStack gap={4}>
        <SectionHeader
          title="My conversations"
          action={
            dashboard.userConversations.length > 0 ? (
              <astryx.Link href="/dashboard/conversations">View all</astryx.Link>
            ) : undefined
          }
        />
        <DataTable
          loading={false}
          data={dashboard.userConversations}
          columns={columns}
          idKey="id"
          pagination={{ page, totalPages: pages, onChange: onPageChange }}
          emptyState={{
            icon: <astryx.Icon icon={Sparkles} />,
            headline: "No conversations yet",
            subtext: currentUser.admin
              ? "Add a script template to launch your first AI-guided screen."
              : "Conversations you run will show up here.",
            action: currentUser.admin ? (
              <astryx.Button
                label="Create a script template"
                variant="secondary"
                href="/admin/script_templates/new"
              />
            ) : undefined,
          }}
        />
      </astryx.VStack>
    </astryx.Card>
  );
};

export default UserConversations;
